import { bankBehaviourAnswerReadSchema, type Behaviour } from "@hakili/contracts";
import { t } from "@hakili/i18n";
import { Button, Callout, Modal } from "@hakili/ui";
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";

import { COMPOSER_WARN_ICON } from "../routes/composer.js";
import { D2Chip } from "../routes/d2-parts.js";
import { behaviourViewOf, type BehaviourLine } from "./view.js";

/**
 * `S1-a` COMPORTEMENT DU COMPTE (`D2-behaviour.md`, frames 201 to 211): **the content of the tab « Comportement du compte »**, bound to `GET /api/v1/bank/files/{id}/behaviour` through the contract's READ twin
 * (`bankBehaviourAnswerReadSchema`: an extra key is tolerated, a missing or mistyped one is the failed read).
 *
 * - **A47: the read is made when the tab opens**, not at every stage's entry; it is held with the file it was made for (`fileId`), and an answer that arrives for a file that is no longer the one drawn is dropped
 *   (latest wins), so nothing of file X is ever handed to file Y.
 * - **Never silent** (A5): a refused, a failed or a contract-breaking answer is frame 210's notice with « Réessayer »; the page is never half drawn.
 * - **Served, not computed** (D-1020-7): every word and figure comes from `behaviourViewOf`, the one place the answer becomes text.
 * - **Nothing for what is not served**: the sections « Ce qui sort », « Virements réguliers », « Incidents », their tiles and totals are not drawn, and no placeholder stands for them.
 */

type Held =
  | { readonly fileId: string; readonly status: "loading" }
  | { readonly fileId: string; readonly status: "failed" }
  | { readonly fileId: string; readonly status: "read"; readonly behaviour: Behaviour };

export function BehaviourTab(props: {
  readonly fileId: string;
  /** The door's read for this file; the tab parses it itself. */
  readonly read: () => Promise<unknown>;
}): ReactElement {
  const { fileId, read } = props;
  const [held, setHeld] = useState<Held>({ fileId, status: "loading" });
  const latest = useRef(0);
  const readRef = useRef(read);
  readRef.current = read;

  const load = useCallback(() => {
    const mine = ++latest.current;
    setHeld({ fileId, status: "loading" });
    readRef
      .current()
      .then((body) => {
        if (latest.current !== mine) return;
        const parsed = bankBehaviourAnswerReadSchema.safeParse(body);
        setHeld(
          parsed.success
            ? { fileId, status: "read", behaviour: parsed.data.behaviour }
            : { fileId, status: "failed" },
        );
      })
      .catch(() => {
        if (latest.current === mine) setHeld({ fileId, status: "failed" });
      });
  }, [fileId]);

  useEffect(() => {
    load();
    return () => {
      // A switch of file (or an unmount) retires the read in flight: its answer is dropped.
      latest.current += 1;
    };
  }, [load]);

  // Whatever is held belongs to the file it was read for; for any other file the tab is reading.
  const current: Held = held.fileId === fileId ? held : { fileId, status: "loading" };

  return (
    <section data-testid="bank-comportement" aria-label={t("bank.comportement.titre")}>
      <h2 className="m-0 text-[length:var(--hk-fs-h2,20px)] font-semibold">{t("bank.comportement.titre")}</h2>
      {current.status === "loading" ? (
        <p role="status" className="mt-4 text-gray-600" data-testid="bank-comportement-chargement">
          {t("bank.comportement.chargement")}
        </p>
      ) : current.status === "failed" ? (
        <div role="alert" className="mt-4">
          <Callout tone="warn" icon={COMPOSER_WARN_ICON} testId="bank-comportement-indisponible">
            <b className="block">{t("bank.comportement.indisponibleTitre")}</b>
            <p className="mt-1">{t("bank.comportement.indisponibleCorps")}</p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              onClick={load}
              data-testid="bank-comportement-reessayer"
            >
              {t("common.action.reessayer")}
            </Button>
          </Callout>
        </div>
      ) : (
        <Page behaviour={current.behaviour} />
      )}
    </section>
  );
}

/**
 * The lines a fact was read from, behind « Voir la ligne du relevé » (frames 203, 207). The kit's `Modal` draws the dialog; what it does not do (the record's keyboard walk, §8) is done here without touching it:
 * the focus enters on « Retour au comportement du compte », Tab stays inside, Escape closes, and the focus returns to the very control that opened it.
 */
function LinesDialog(props: {
  readonly lines: readonly BehaviourLine[];
  readonly opener: HTMLElement | null;
  readonly onClose: () => void;
}): ReactElement {
  const { lines, opener, onClose } = props;
  useEffect(() => {
    document.querySelector<HTMLElement>('[data-testid="bank-comportement-ligne-retour"]')?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = document.querySelector<HTMLElement>('[data-testid="bank-comportement-ligne-dialogue"]');
      const stops =
        dialog === null
          ? []
          : [...dialog.querySelectorAll<HTMLElement>("button, [href], [tabindex]:not([tabindex='-1'])")];
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus();
    };
  }, [onClose, opener]);
  return (
    <Modal
      open
      title={lines[0]?.where ?? t("bank.comportement.voirLigne")}
      cross={false}
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={onClose}

          data-testid="bank-comportement-ligne-retour"
        >
          {t("bank.comportement.retourLigne")}
        </Button>
      }
    >
      <div data-testid="bank-comportement-ligne-dialogue">
        <ul className="m-0 list-none p-0">
          {lines.map((line) => (
            <li key={line.id} className="border-b border-gray-100 py-2">
              <span className="block font-medium">{line.label}</span>
              <span className="mt-1 flex flex-wrap gap-x-4 text-[color:var(--hk-text-2)]">
                <span>{line.date}</span>
                <span>
                  <span className="font-medium">{t("bank.comportement.montant")}</span> {line.amount}
                </span>
                {line.balance === null ? null : (
                  <span>
                    <span className="font-medium">{t("bank.comportement.solde")}</span> {line.balance}
                  </span>
                )}
              </span>
              {lines.length > 1 ? (
                <span className="block text-[color:var(--hk-text-2)]">{line.where}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

function Page(props: { readonly behaviour: Behaviour }): ReactElement {
  const view = behaviourViewOf(props.behaviour);
  const [trace, setTrace] = useState<{
    readonly ids: readonly string[];
    readonly opener: HTMLElement | null;
  } | null>(null);
  const closeTrace = useCallback(() => {
    setTrace(null);
  }, []);
  // The opener is the control that was pressed, named by its test id: the kit's `Button` hands no event, and a click does not focus a button in every browser.
  const openTrace = (ids: readonly string[], testId: string) => (): void => {
    setTrace({ ids, opener: document.querySelector<HTMLElement>(`[data-testid="${testId}"]`) });
  };
  if (view.kind === "not_yet_read") {
    return (
      <div className="mt-4">
        <Callout tone="info" testId="bank-comportement-non-lu">
          <b className="block">{t("bank.comportement.nonLuTitre")}</b>
          <p className="mt-1">{t("bank.comportement.nonLuCorps")}</p>
        </Callout>
      </div>
    );
  }
  const traceLink = (testId: string, ids: readonly string[]): ReactElement => (
    <Button
      variant="secondary"
      size="sm"
      className="mt-3"
      onClick={openTrace(ids, testId)}
      data-testid={testId}
    >
      {t("bank.comportement.voirLigne")}
    </Button>
  );
  return (
    <div className="mt-4 flex flex-col gap-4">
      <div>
        <D2Chip tone="brand" testId="bank-comportement-fenetre">
          {view.chip}
        </D2Chip>
        <p className="m-0 mt-1 text-[color:var(--hk-text-2)]">{t("bank.comportement.faits")}</p>
      </div>
      {view.partialNotice === null ? null : (
        <Callout tone="info" testId="bank-comportement-partiel">
          <p className="m-0">{view.partialNotice}</p>
        </Callout>
      )}
      {view.withheld === null ? null : (
        <Callout tone="warn" icon={COMPOSER_WARN_ICON} testId="bank-comportement-retenu">
          <p className="m-0">{view.withheld}</p>
        </Callout>
      )}
      {view.hero === null ? null : (
        <div
          className="rounded-lg border border-indigo-200 bg-indigo-50 px-5 py-4"
          data-testid="bank-comportement-hero"
          data-focus={view.hero.kind}
        >
          {view.hero.kind === "low_point" ? (
            <p className="m-0 mb-1 text-[length:var(--hk-fs-meta,13px)] font-semibold uppercase tracking-wide text-indigo-700">
              {t("bank.comportement.heroSurtitre")}
            </p>
          ) : null}
          <p className="m-0 text-lg font-semibold">{view.hero.sentence}</p>
        </div>
      )}
      {view.lowestEve === null ? null : (
        <div
          className="rounded-lg border border-gray-200 bg-white px-5 py-4"
          data-testid="bank-comportement-veille"
        >
          <p className="m-0 font-semibold">{view.lowestEve.amount}</p>
          {view.lowestEve.share === null ? null : <p className="m-0 mt-1">{view.lowestEve.share}</p>}
          {view.lowestEve.others.length === 0 ? null : (
            <ul className="m-0 mt-2 list-none p-0 text-[color:var(--hk-text-2)]">
              {view.lowestEve.others.map((one) => (
                <li key={one}>{one}</li>
              ))}
            </ul>
          )}
          {traceLink("bank-comportement-ligne-veille", view.lowestEve.lineIds)}
        </div>
      )}
      {view.income.length === 0 ? null : (
        <section data-testid="bank-comportement-entrees" aria-label={t("bank.comportement.sectionEntrees")}>
          <h3 className="m-0 mb-2 text-base font-semibold">{t("bank.comportement.sectionEntrees")}</h3>
          <table className="w-full text-left">
            <thead>
              <tr className="text-[color:var(--hk-text-2)]">
                <th scope="col">{t("bank.comportement.colMois")}</th>
                <th scope="col">{t("bank.comportement.colTotal")}</th>
                <th scope="col">{t("bank.comportement.colSalaire")}</th>
                <th scope="col">{t("bank.comportement.colJour")}</th>
              </tr>
            </thead>
            <tbody>
              {view.income.map((row) => (
                <tr key={row.month}>
                  <th scope="row" className="font-medium">
                    {row.month}
                  </th>
                  <td>{row.total}</td>
                  <td>{row.salary ?? ""}</td>
                  <td>{row.salaryDay === null ? "" : String(row.salaryDay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {view.loanlike.length === 0 ? null : (
        <section data-testid="bank-comportement-pret" aria-label={t("bank.comportement.sectionPret")}>
          <h3 className="m-0 mb-2 text-base font-semibold">{t("bank.comportement.sectionPret")}</h3>
          <ul className="m-0 list-none p-0">
            {view.loanlike.map((one, index) => (
              <li
                key={`${one.label}-${String(index)}`}
                className="rounded-lg border border-gray-200 bg-white px-5 py-4"
              >
                <span className="block font-medium">{one.label}</span>
                <span className="block text-[color:var(--hk-text-2)]">
                  {one.amount}
                  {one.day === null
                    ? ""
                    : ` · ${t("bank.comportement.jourDuMois").replace("{jour}", String(one.day))}`}
                  {` · ${String(one.monthsSeen)} mois`}
                </span>
                {traceLink(`bank-comportement-ligne-pret-${String(index)}`, one.lineIds)}
              </li>
            ))}
          </ul>
        </section>
      )}
      {trace === null ? null : (
        <LinesDialog lines={view.linesOf(trace.ids)} opener={trace.opener} onClose={closeTrace} />
      )}
    </div>
  );
}
